/**
 * Decode a storage-relative path when it already contains percent-encoding.
 * Mirrors backend FileUtils::decodeStorageRelativePath.
 */
export function decodeStorageRelativePath(path) {
  if (typeof path !== 'string' || path === '') {
    return path;
  }

  const normalized = path.replace(/\\/g, '/');
  if (!/%[0-9A-Fa-f]{2}/.test(normalized)) {
    return normalized;
  }

  try {
    const decoded = decodeURIComponent(normalized);
    if (decoded !== '') {
      return decoded;
    }
  } catch (_) {
    // Malformed percent-encoding — keep the original path.
  }

  return normalized;
}

/**
 * Build a /storage/... URL with each path segment encoded.
 * Mirrors backend FileUtils::publicStorageUrl.
 */
export function publicStorageUrl(relativePath) {
  if (typeof relativePath !== 'string' || relativePath === '') {
    return null;
  }

  let path = decodeStorageRelativePath(relativePath).replace(/^\/+/, '');
  if (path.toLowerCase().indexOf('storage/') === 0) {
    path = path.slice('storage/'.length);
  }

  const encoded = path
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/');

  return `/storage/${encoded}`;
}

/**
 * Normalize a stored public file value for use as an img src / srcSet URL.
 * For /storage/... paths (optional http(s) or protocol-relative host), decode
 * then encode per segment. Non-/storage/ values pass through unchanged.
 * Mirrors backend FileUtils::normalizePublicStorageUrl for /storage/ URLs;
 * FE keeps non-storage pass-through per NNO-5442.
 */
export function normalizePublicStorageUrl(storedPath) {
  if (typeof storedPath !== 'string' || storedPath === '') {
    return storedPath;
  }

  const normalized = storedPath.replace(/\\/g, '/').trim();
  if (normalized === '') {
    return storedPath;
  }

  const match = normalized.match(
    /^(https?:\/\/[^/]+|\/\/[^/]+)?(\/storage\/)(.+)$/i
  );
  if (!match) {
    return storedPath;
  }

  const hostPrefix = match[1] || '';
  return `${hostPrefix}${publicStorageUrl(match[3])}`;
}
